import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import Layout from "@/components/Layout";
import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import heroImg from "@/assets/services-hero.jpg";

const formSchema = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().min(1, "Last name is required").max(100),
  company: z.string().trim().max(200).optional(),
  title: z.string().trim().max(100).optional(),
  phone: z.string().trim().max(30).optional(),
  email: z.string().trim().email("Please enter a valid email").max(255),
  comments: z.string().trim().max(1000).optional(),
  action: z.enum(["subscribe", "unsubscribe"]),
  privacyConsent: z.literal(true, {
    errorMap: () => ({ message: "You must agree to the privacy policy" }),
  }),
});

type FormValues = z.infer<typeof formSchema>;

const NewsletterSignupPage = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      title: "",
      phone: "",
      email: "",
      comments: "",
      action: "subscribe",
      privacyConsent: false as unknown as true,
    },
  });

  const onSubmit = async (data: FormValues) => {
    const { error } = await supabase.functions.invoke("manage-newsletter-subscription", {
      body: data,
    });

    if (error) {
      toast({
        variant: "destructive",
        title: "We couldn't process your request",
        description: "Please try again in a moment.",
      });
      return;
    }

    setSubmitted(true);
    toast({
      title: data.action === "subscribe" ? "Successfully subscribed!" : "Successfully unsubscribed",
      description:
        data.action === "subscribe"
          ? "You'll receive our latest news and insights."
          : "You've been removed from our mailing list.",
    });
  };

  return (
    <Layout>
      <SEO title="Newsletter Signup | Vaughan McLean" description="Subscribe to the Vaughan McLean newsletter for the latest legal news, firm updates, and industry insights." />
      <PageHero backgroundImage={heroImg} title="Sign Up" />

      <section className="bg-background py-20">
        <div className="container mx-auto px-8 lg:px-16 max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-light text-foreground mb-4">
              Stay Connected.
            </h2>
            <p className="font-body text-muted-foreground mb-12">
              Sign up below to receive our upcoming news, insights and invaluable
              resources delivered straight to you!
            </p>

            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-16 border border-border rounded-sm"
              >
                <h3 className="font-heading text-2xl text-foreground mb-3">
                  Thank You
                </h3>
                <p className="font-body text-muted-foreground">
                  Your request has been received. We'll be in touch soon.
                </p>
                <Button
                  variant="outline"
                  className="mt-6 border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                  onClick={() => {
                    setSubmitted(false);
                    form.reset();
                  }}
                >
                  Submit Another Response
                </Button>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-body text-sm">
                            First Name<span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-body text-sm">
                            Last Name<span className="text-destructive">*</span>
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-body text-sm">
                          Company / Organization
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-body text-sm">
                            Title
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground font-body text-sm">
                            Phone
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              type="tel"
                              className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-body text-sm">
                          Email<span className="text-destructive">*</span>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="comments"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-body text-sm">
                          Comments or Questions
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={4}
                            className="bg-card border-border text-foreground font-body rounded-sm focus-visible:ring-primary resize-none"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="action"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground font-body text-sm">
                          Action
                        </FormLabel>
                        <div className="flex gap-6 mt-2">
                          <label className="flex items-center gap-2 cursor-pointer font-body text-sm text-foreground">
                            <input
                              type="radio"
                              value="subscribe"
                              checked={field.value === "subscribe"}
                              onChange={() => field.onChange("subscribe")}
                              className="accent-primary"
                            />
                            Subscribe
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer font-body text-sm text-foreground">
                            <input
                              type="radio"
                              value="unsubscribe"
                              checked={field.value === "unsubscribe"}
                              onChange={() => field.onChange("unsubscribe")}
                              className="accent-primary"
                            />
                            Unsubscribe
                          </label>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="privacyConsent"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary mt-1"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-muted-foreground font-body text-xs leading-relaxed font-normal">
                            You agree that Vaughan McLean may collect, use and disclose
                            your personal data which you have provided in this form, for
                            providing marketing material that you have agreed to receive,
                            in accordance with our data privacy policy.<span className="text-destructive">*</span>
                          </FormLabel>
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  <p className="text-xs text-muted-foreground font-body">
                    <span className="text-destructive">*</span> Indicates a required field.
                  </p>

                  <Button
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-body tracking-wider uppercase text-sm px-10 py-3 rounded-sm"
                  >
                    {form.formState.isSubmitting ? "Submitting…" : "Submit"}
                  </Button>
                </form>
              </Form>
            )}
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default NewsletterSignupPage;
